// Custom modal dialogs styled with the app theme.
// Replaces native prompt() / confirm() — those use system UI which
// looks foreign inside a dark PWA.

import { el } from './dom.js';

type ConfirmOpts = {
  title: string;
  message?: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type PromptField = {
  key: string;
  label: string;
  value: string;
  inputMode?: 'decimal' | 'numeric' | 'text';
  step?: string;
  min?: string;
};

type PromptOpts = {
  title: string;
  message?: string;
  fields: PromptField[];
  okLabel?: string;
  cancelLabel?: string;
  destructiveLabel?: string; // optional third button (e.g. "Apagar")
};

// onDismiss is called when the user presses Escape or taps the backdrop —
// the dialog resolves it as a cancel. Mobile expects tap-outside to dismiss.
function mountModal(content: HTMLElement, onDismiss: () => void): () => void {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const wrapper = el('div', { class: 'modal', role: 'dialog' } as never, [content]);
  wrapper.setAttribute('aria-modal', 'true');
  backdrop.append(wrapper);

  const prevFocus = document.activeElement as HTMLElement | null;

  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) onDismiss();
  });
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') onDismiss();
  };
  document.addEventListener('keydown', onKey);

  document.body.append(backdrop);
  // Block scroll
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  return () => {
    document.removeEventListener('keydown', onKey);
    backdrop.remove();
    document.body.style.overflow = prevOverflow;
    prevFocus?.focus?.();
  };
}

export function confirmDialog(opts: ConfirmOpts): Promise<boolean> {
  return new Promise((resolve) => {
    let close = () => {};
    const onCancel = () => {
      close();
      resolve(false);
    };
    const onOk = () => {
      close();
      resolve(true);
    };

    const cancelBtn = el('button', { class: 'btn', type: 'button', onclick: onCancel }, [
      opts.cancelLabel ?? 'Cancelar',
    ]);
    const okBtn = el(
      'button',
      {
        class: `btn ${opts.danger ? 'btn--danger' : 'btn--primary'}`,
        type: 'button',
        onclick: onOk,
      },
      [opts.okLabel ?? 'OK'],
    );

    const body = el(
      'div',
      { class: 'modal__body' },
      [
        el('h3', { class: 'modal__title' }, [opts.title]),
        opts.message ? el('p', { class: 'modal__message' }, [opts.message]) : null,
        el('div', { class: 'modal__actions' }, [cancelBtn, okBtn]),
      ].filter(Boolean) as Node[],
    );

    close = mountModal(body, onCancel);
    setTimeout(() => cancelBtn.focus(), 50);
  });
}

export function promptDialog<T extends Record<string, string>>(
  opts: PromptOpts,
): Promise<{ ok: boolean; values: T; destructive?: boolean }> {
  return new Promise((resolve) => {
    let close = () => {};

    const inputs: Record<string, HTMLInputElement> = {};
    const fieldNodes: HTMLElement[] = opts.fields.map((f) => {
      const input = el('input', {
        class: 'input',
        type: 'text',
        inputMode: f.inputMode ?? 'text',
        step: f.step,
        min: f.min,
        value: f.value,
        'aria-label': f.label,
      }) as HTMLInputElement;
      inputs[f.key] = input;
      return el('div', { class: 'field' }, [
        el('div', { class: 'field__label' }, [f.label]),
        input,
      ]);
    });

    const onCancel = () => {
      close();
      resolve({ ok: false, values: {} as T });
    };
    const onOk = () => {
      const values: Record<string, string> = {};
      for (const [k, inp] of Object.entries(inputs)) values[k] = inp.value;
      close();
      resolve({ ok: true, values: values as T });
    };
    const onDestructive = () => {
      close();
      resolve({ ok: false, destructive: true, values: {} as T });
    };

    const actions: HTMLElement[] = [];
    if (opts.destructiveLabel) {
      actions.push(
        el('button', { class: 'btn btn--danger', type: 'button', onclick: onDestructive }, [
          opts.destructiveLabel,
        ]),
      );
    }
    actions.push(
      el('button', { class: 'btn', type: 'button', onclick: onCancel }, [
        opts.cancelLabel ?? 'Cancelar',
      ]),
    );
    actions.push(
      el('button', { class: 'btn btn--primary', type: 'button', onclick: onOk }, [
        opts.okLabel ?? 'OK',
      ]),
    );

    const form = el(
      'form',
      {
        onsubmit: (ev: Event) => {
          ev.preventDefault();
          onOk();
        },
      },
      [
        el('h3', { class: 'modal__title' }, [opts.title]),
        opts.message ? el('p', { class: 'modal__message' }, [opts.message]) : null,
        el('div', { class: 'stack stack--md', style: 'margin-top: var(--space-3)' }, fieldNodes),
        el('div', { class: 'modal__actions' }, actions),
      ].filter(Boolean) as Node[],
    );

    close = mountModal(el('div', { class: 'modal__body' }, [form]), onCancel);

    // Focus the first input
    const first = Object.values(inputs)[0];
    if (first) {
      setTimeout(() => {
        first.focus();
        first.select();
      }, 50);
    }
  });
}
